import { Close } from '@mui/icons-material';
import { Drawer, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { OfficeMap } from '../../../common/OfficeMap/OfficeMap';
import { useSnackbarContext } from '../../../common/SnackbarProvider/SnackbarProvider';
import { officeSpaceBookingApi } from '../../../../api/officeSpaceBookingApi';
import { TeamAssignmentList } from '../../DeskManagementLayout/TeamAssignmentList/TeamAssignmentList';
import BookingDate from '../BookingDate/BookingDate';
import { isMultiDayBooking } from '../../../../constants';

type UpdateBookingDrawerProps = {
    open: boolean;
    bookingId: string;
    onClose: () => void;
};

export const UpdateBookingDrawer: React.FC<UpdateBookingDrawerProps> = ({
    onClose,
    open,
    bookingId,
}) => {
    const [selectedSpace, setSelectedSpace] = useState<string>('');
    const [bookingStartDate, setBookingStartDate] = useState<string | null>();
    const [bookingUserId, setBookingUserId] = useState<string>('');
    const [bookingEndDate, setBookingEndDate] = useState<string | null>('');

    const [getBookings] = officeSpaceBookingApi.useLazyListQuery();
    const [updateBooking, { isLoading }] = officeSpaceBookingApi.useUpdateMutation();

    const { showSnackbar } = useSnackbarContext();

    const handleOfficeSpaceClick = (value: string) => {
        setSelectedSpace(value);
    };

    const handleClose = () => {
        setSelectedSpace('');
        onClose();
    };

    const handleSubmit = async () => {
        updateBooking({
            bookingId,
            spaceName: selectedSpace,
        })
            .unwrap()
            .then(() => {
                showSnackbar('Booking was successfully updated', 'success');
                handleClose();
            })
            .catch((error: { status: number }) => {
                if (error.status === 403) {
                    showSnackbar("User's team can not book this office space", 'error');
                }
            });
    };

    useEffect(() => {
        if (bookingId) {
            const fetchBookings = async () => {
                try {
                    const { data } = await getBookings({ filters: { id: [bookingId] } }).unwrap();
                    const startDate = data[0].startDate;
                    const endDate = data[0].endDate;
                    const userId = data[0].user.id;

                    setBookingStartDate(startDate);
                    setBookingEndDate(endDate);
                    setBookingUserId(userId);
                } catch {
                    showSnackbar('Error occured while getting a booking', 'error');
                }
            };

            fetchBookings();
        }
    }, [bookingId]);

    return (
        <Drawer open={open} onClose={handleClose}>
            <Stack p={3} width="100vw" height="100%">
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">Update Booking</Typography>
                    <Close onClick={handleClose} sx={{ cursor: 'pointer' }} />
                </Stack>
                <Stack marginTop={1}>
                    <BookingDate
                        startDateState={bookingStartDate}
                        endDateState={bookingEndDate}
                        editWorkPlaceActive
                        periodActive={isMultiDayBooking(bookingStartDate, bookingEndDate)}
                    />
                </Stack>
                <Stack marginTop={1}>
                    <TeamAssignmentList />
                </Stack>
                <Stack flexGrow={1}>
                    <OfficeMap
                        applyDefaultConfig
                        selectedSpace={selectedSpace}
                        onOfficeSpaceClick={handleOfficeSpaceClick}
                        startDate={bookingStartDate}
                        endDate={bookingEndDate}
                        userId={bookingUserId}
                        showBookerDataForBookedDesks={
                            !isMultiDayBooking(bookingStartDate, bookingEndDate)
                        }
                    />
                </Stack>
                {selectedSpace && (
                    <LoadingButton
                        onClick={handleSubmit}
                        loading={isLoading}
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            backgroundColor: '#40a7e3',
                            color: '#ffffff',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                            ':hover': {
                                backgroundColor: '#40a7e3',
                            },
                        }}
                    >
                        Change to {selectedSpace}
                    </LoadingButton>
                )}
            </Stack>
        </Drawer>
    );
};
